'use client';

import { TelegramProductShortResponseDto } from '@/generated';
import { ProductBigPreviewMemo } from '@/app/[lang]/(main)/components/shared/Product/productBigPreview.memo';
import clsx from 'clsx';
import { SortSwitch } from '../../Inputs/SortSwitch';
import { GeoSelector } from '../../GeoSelector';
import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { NotFound } from './NotFound';
import { useMediaQuerySafe } from '@/hooks/useMediaQuerySafe';
import { arrangeItemsExtended } from '@/utils/sortProducts';
import { sendGTMEvent } from '@next/third-parties/google';
import { AdViewSource } from '@/utils/adViewSource';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface ProductsListProps {
  products: TelegramProductShortResponseDto[];
  showMore?: boolean;
  isLoading?: boolean;
  sort: string;
  onChangeSort: (newSort: string) => void;
  onClickLoadMore?: () => void;
  hasTextSearch?: boolean;
  isWideInitially?: boolean;
}

function ProductsListClientInternal({
  products,
  showMore = false,
  isLoading = false,
  onClickLoadMore,
  sort,
  onChangeSort,
  hasTextSearch,
  isWideInitially = false,
}: ProductsListProps) {
  const t = useTranslations();
  const isThreeElems = useMediaQuerySafe('(min-width: 768px)', isWideInitially);
  const productsArranged = useMemo(() => {
    const itemsArrayed = arrangeItemsExtended(products, isThreeElems ? 3 : 2, showMore);
    return itemsArrayed.flat(2);
  }, [products, isThreeElems, showMore]);
  if (products.length === 0 && !isLoading) {
    return <NotFound />;
  }

  return (
    <>
      <div className='gap-[4px] mt-[12px] mb-[12px] lg:flex hidden'>
        <SortSwitch selected={sort} onChange={onChangeSort} />
      </div>
      <div className='flex lg:hidden justify-between items-center my-[16px]'>
        <GeoSelector />

        <div className='flex gap-[8px]'>
          <SortSwitch selected={sort} onChange={onChangeSort} isMobile={true} />
        </div>
      </div>
      <div className='w-full grid md:grid-cols-3 grid-cols-2 md:gap-[16px] gap-[12px] gap-y-[16px]'>
        {productsArranged.map((product, index) => (
          <div className={clsx({ 'col-[span_2]': product.isPremium })} key={product.id}>
            <ProductBigPreviewMemo
              product={product}
              priority={index < 4}
              adViewSource={hasTextSearch ? AdViewSource.SearchWithText : AdViewSource.SearchWithoutText}
            />
          </div>
        ))}
      </div>

      {showMore && !isLoading && (
        <Button
          onClick={() => {
            sendGTMEvent({
              event: 'show_more_button_search_page',
            });
            onClickLoadMore?.();
          }}
          className='w-[350px] h-[44px] mt-[16px] justify-self-center md:mt-[40px] bg-text-primary-day hover:bg-text-primary-day/90'
        >
          {t('index.show_more')}
        </Button>
      )}

      {isLoading && (
        <div className='grid md:grid-cols-3 grid-cols-2 md:gap-[16px] gap-[8px] mt-[16px]'>
          <Skeleton className='h-[175px] w-full' />
          <Skeleton className='h-[175px] w-full' />
          <Skeleton className='h-[175px] w-full' />
        </div>
      )}
    </>
  );
}

export const ProductsListClient = React.memo(ProductsListClientInternal);
